/**
 * RoleScopedDashboardsPage — `/reporting/role-scoped`.
 *
 * Wired to GET /v1/reporting/role-dashboards on service-reporting-api.
 * Mappings live in `reporting.role_dashboard` (seeded in
 * prisma/reporting/db-migrations/20260509_e2e_analytics_extras.sql).
 */
import { type JSX } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Users } from 'lucide-react';

import { ApiError } from '../../lib/api/client';
import { listRoleDashboards, type RoleDashboardWire } from '../../lib/api/reporting';
import { DeferredNotice, PageHeader } from '../shared/StaffUI';

export function RoleScopedDashboardsPage(): JSX.Element {
  const rows = useQuery<readonly RoleDashboardWire[], Error>({
    queryKey: ['reporting', 'role-dashboards'],
    queryFn: () => listRoleDashboards(),
    retry: false,
  });

  const denied =
    rows.isError && rows.error instanceof ApiError &&
    (rows.error.status === 401 || rows.error.status === 403);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Role-scoped dashboards"
        description="Default dashboard binding per operator role."
      />

      {denied && (
        <DeferredNotice title="Permission required">
          <code className="font-mono">Reporting/readRoleDashboards</code> required.
        </DeferredNotice>
      )}

      {rows.isError && !denied && (
        <DeferredNotice title="Service temporarily unavailable">
          {rows.error.message}
        </DeferredNotice>
      )}

      {!denied && (
        <div className="bg-white border border-border rounded-xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <Users className="w-4 h-4 text-nipost-green" />
            <h3 className="font-semibold text-gray-900">
              Mapping ({rows.data?.length ?? 0})
            </h3>
          </div>
          {rows.isPending ? (
            <p className="text-sm text-muted">Loading…</p>
          ) : (rows.data?.length ?? 0) === 0 ? (
            <p className="text-sm text-muted py-4 text-center">No role-dashboard mappings configured.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="role-dashboard-table">
                <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-muted">
                  <tr>
                    <th className="text-left px-4 py-2">Role</th>
                    <th className="text-left px-4 py-2">Default dashboard</th>
                    <th className="text-right px-4 py-2">Tiles</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {(rows.data ?? []).map((r) => (
                    <tr key={r.id} data-testid="role-dashboard-row">
                      <td className="px-4 py-2 font-mono text-xs">{r.roleKey}</td>
                      <td className="px-4 py-2 text-xs">{r.dashboardLabel}</td>
                      <td className="px-4 py-2 text-right text-xs">{r.tileCount}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
